/**
 * Normalization and persistence helpers for form-level presentation and
 * submitted-list defaults. These settings intentionally live inside the
 * versioned React tab schema so legacy backend DTOs and database tables remain
 * backward-compatible.
 */
import type {
  DynamicFormDefinition,
  FormTitleAlignment,
  SubmissionListColumn,
  SubmissionListSettings,
  SubmissionSystemColumns
} from '../models/form';

export const FORM_SETTINGS_VERSION = 2;
export const MAX_DEFAULT_SUBMISSION_COLUMNS = 20;

export const DEFAULT_SUBMISSION_SYSTEM_COLUMNS: Readonly<SubmissionSystemColumns> = Object.freeze({
  ShowId: true,
  ShowSubmitCode: true,
  ShowFilledOn: true,
  ShowIpAddress: true
});

export interface PersistedFormSettingsV2 {
  version: 2;
  titleAlignment: FormTitleAlignment;
  titleColor: string;
  showHeader: boolean;
  headerBackgroundColor: string;
  showDateRange: boolean;
  submissionListSettings: SubmissionListSettings;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function boolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return fallback;
}

export function normalizeTitleAlignment(value: unknown): FormTitleAlignment {
  return value === 'right' || value === 'center' || value === 'left' || value === 'inherit'
    ? value
    : 'inherit';
}

/** Accept only a CSS-safe six-digit hexadecimal color. */
function normalizeHexColor(value: unknown): string {
  const text = typeof value === 'string' ? value.trim() : '';
  return /^#[0-9a-f]{6}$/i.test(text) ? text.toLowerCase() : '';
}

/** Accept only a CSS-safe six-digit hexadecimal title color. */
export function normalizeFormTitleColor(value: unknown): string {
  return normalizeHexColor(value);
}

/** Accept only a CSS-safe six-digit hexadecimal heading background color. */
export function normalizeFormHeaderBackgroundColor(value: unknown): string {
  return normalizeHexColor(value);
}

export function normalizeSubmissionSystemColumns(value: unknown): SubmissionSystemColumns {
  const source = record(value);
  return {
    ShowId: boolean(source.ShowId, DEFAULT_SUBMISSION_SYSTEM_COLUMNS.ShowId),
    ShowSubmitCode: boolean(source.ShowSubmitCode, DEFAULT_SUBMISSION_SYSTEM_COLUMNS.ShowSubmitCode),
    ShowFilledOn: boolean(source.ShowFilledOn, DEFAULT_SUBMISSION_SYSTEM_COLUMNS.ShowFilledOn),
    ShowIpAddress: boolean(source.ShowIpAddress, DEFAULT_SUBMISSION_SYSTEM_COLUMNS.ShowIpAddress)
  };
}

export function normalizeSubmissionListColumns(value: unknown): SubmissionListColumn[] {
  if (!Array.isArray(value)) return [];
  const output: SubmissionListColumn[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const source = record(item);
    const model = typeof source.Model === 'string' ? source.Model.trim() : '';
    const name = typeof source.Name === 'string' ? source.Name.trim() : '';
    if (!model || seen.has(model)) continue;
    seen.add(model);
    output.push({ Model: model.slice(0, 250), Name: (name || model).slice(0, 500) });
    if (output.length >= MAX_DEFAULT_SUBMISSION_COLUMNS) break;
  }
  return output;
}

export function normalizeSubmissionListSettings(value: unknown): SubmissionListSettings {
  const source = record(value);
  return {
    ...normalizeSubmissionSystemColumns(source),
    Columns: normalizeSubmissionListColumns(source.Columns)
  };
}

export function defaultSubmissionListSettings(): SubmissionListSettings {
  return {
    ...DEFAULT_SUBMISSION_SYSTEM_COLUMNS,
    Columns: []
  };
}

export function createPersistedFormSettings(form: DynamicFormDefinition): PersistedFormSettingsV2 {
  return {
    version: FORM_SETTINGS_VERSION,
    titleAlignment: normalizeTitleAlignment(form.TitleAlignment),
    titleColor: normalizeFormTitleColor(form.TitleColor),
    showHeader: boolean(form.ShowHeader, true),
    headerBackgroundColor: normalizeFormHeaderBackgroundColor(form.HeaderBackgroundColor),
    showDateRange: boolean(form.ShowDateRange, true),
    submissionListSettings: normalizeSubmissionListSettings(form.SubmissionListSettings)
  };
}

/**
 * Reads v1, v2 and incomplete legacy metadata. Missing v2 properties preserve
 * the pre-v2 public behavior: the heading remains visible and uses the theme's
 * original background color.
 */
export function normalizePersistedFormSettings(value: unknown): PersistedFormSettingsV2 {
  const source = record(value);
  return {
    version: FORM_SETTINGS_VERSION,
    titleAlignment: normalizeTitleAlignment(source.titleAlignment),
    titleColor: normalizeFormTitleColor(source.titleColor),
    showHeader: boolean(source.showHeader, true),
    headerBackgroundColor: normalizeFormHeaderBackgroundColor(source.headerBackgroundColor),
    showDateRange: boolean(source.showDateRange, true),
    submissionListSettings: normalizeSubmissionListSettings(source.submissionListSettings)
  };
}

export function submissionSystemColumnsEqual(a: SubmissionSystemColumns, b: SubmissionSystemColumns): boolean {
  return a.ShowId === b.ShowId
    && a.ShowSubmitCode === b.ShowSubmitCode
    && a.ShowFilledOn === b.ShowFilledOn
    && a.ShowIpAddress === b.ShowIpAddress;
}
